import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeams } from '@/hooks/useTeams';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRightLeft, Loader2 } from 'lucide-react';

interface TicketTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  currentTeamId?: string;
  onTransferComplete?: () => void;
}

export function TicketTransferDialog({
  open,
  onOpenChange,
  ticketId,
  currentTeamId,
  onTransferComplete,
}: TicketTransferDialogProps) {
  const { teams, isLoading: teamsLoading } = useTeams();
  // FIX-R44-CRIT-1 / R44-LOW-1: pull activeShop so the ticket update can
  // be explicitly shop-scoped (defense-in-depth above RLS) and the
  // audit_log row anchors on the trusted active context rather than
  // `teams[0]?.shop_id` (which is order-dependent and brittle).
  const { user, activeShop } = useAuth();
  const [targetTeamId, setTargetTeamId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  const activeTeams = teams?.filter(t => t.is_active) ?? [];
  const availableTeams = activeTeams.filter(t => t.id !== currentTeamId);

  const handleTransfer = async () => {
    if (!targetTeamId) {
      toast.error('Please select a target team');
      return;
    }

    if (!activeShop?.id) {
      toast.error('No active shop context');
      return;
    }

    setIsTransferring(true);
    try {
      // Update ticket team_id.
      // FIX-R44-CRIT-1: explicit `.eq('shop_id', activeShop.id)` predicate.
      // RLS already blocks cross-shop ticket writes, but the explicit guard
      // makes the boundary visible in code review and fails closed if RLS
      // is ever loosened.
      const { error: updateError } = await supabase
        .from('tickets')
        .update({ team_id: targetTeamId })
        .eq('id', ticketId)
        .eq('shop_id', activeShop.id);

      if (updateError) throw updateError;

      // Log to audit_log with from_team_id, to_team_id, and reason.
      // FIX-R44-LOW-1: anchor on activeShop.id (trusted context) rather
      // than teams[0]?.shop_id (order-dependent + brittle).
      const { error: auditError } = await supabase.from('audit_log').insert({
        shop_id: activeShop.id,
        actor_user_id: user?.id,
        object_type: 'ticket',
        object_id: ticketId,
        action: 'team_transfer',
        from_team_id: currentTeamId || null,
        to_team_id: targetTeamId,
        reason: reason.trim() || null,
        old_value: currentTeamId ? { team_id: currentTeamId } : null,
        new_value: { team_id: targetTeamId },
      });

      if (auditError) {
        console.error('Failed to log team transfer:', auditError);
      }

      toast.success('Ticket transferred successfully');
      onOpenChange(false);
      onTransferComplete?.();
    } catch (error) {
      console.error('Error transferring ticket:', error);
      toast.error('Failed to transfer ticket');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleClose = () => {
    setTargetTeamId('');
    setReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Transfer Ticket to Another Team
          </DialogTitle>
          <DialogDescription>
            Move this ticket to a different team. This action will be logged for audit purposes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="target-team">Target Team</Label>
            <Select
              value={targetTeamId}
              onValueChange={setTargetTeamId}
              disabled={teamsLoading}
            >
              <SelectTrigger id="target-team">
                <SelectValue placeholder="Select team to transfer to..." />
              </SelectTrigger>
              <SelectContent>
                {availableTeams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                    {team.is_default && ' (Default)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="Why is this ticket being transferred?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isTransferring}>
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!targetTeamId || isTransferring}
          >
            {isTransferring ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Transferring...
              </>
            ) : (
              'Transfer Ticket'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
